import { formatDistance } from "date-fns";
import { es } from "date-fns/locale";
import { capitalize } from "lodash";
import moment from "moment";
import { FC, useCallback, useContext, useEffect, useState } from "react";
import { BsFuelPump } from "react-icons/bs";
import { HiOutlineEye } from "react-icons/hi";
import { ActionIcon, Button, Tooltip } from "rizzui";
import { toast } from "sonner";
import { FormDrawerView } from "../../../components/forms/FormDrawer";
import RefuelDetailModal from "../../../components/forms/RefuelDetailModal";
import { PrivilegeContext } from "../../../components/priviledge/PriviledgeProvider";
import { useFiltersPR } from "../../../components/providers/FiltersProvider";
import { FilterDrawerView } from "../../../components/table/components/TableFilter";
import CustomTable from "../../../components/table/CustomTable";
import useFetch from "../../../hooks/useFetch";
import Page from "../../../layout/Page/Page";
import { RefuelService } from "../../../services/refuels/refuelService";
import { Refuel, RefuelsApiResponse } from "../../../type/entities/refuel-type";
import RefuelForm from "../components/RefuelForm";
import RefuelsFilters from "./RefuelsFilters";
import useHandleErrors from "../../../hooks/useHandleErrors";
import DeletePopover from "../../../components/buttons/DeletePopover";
import { MdOutlineModeEditOutline } from "react-icons/md";

interface RefuelsListProps {
    openFormDrawer: boolean;
    setOpenFormDrawer: React.Dispatch<React.SetStateAction<boolean>>;
    refuelSelected: Refuel | null;
    setRefuelSelected: React.Dispatch<React.SetStateAction<Refuel | null>>;
    registerTableRefetch?: (refetch: () => void) => void;
    setIsOpenImportModal: React.Dispatch<React.SetStateAction<boolean>>;
}

const columnOrder = [
    'id',
    'vehicle',
    'user',
    'liters',
    'totalCost',
    'totalKilometers',
    'date',
    'description',
    'photoCount',
    'createdAt'
];

const RefuelsList: FC<RefuelsListProps> = ({ openFormDrawer, setOpenFormDrawer, refuelSelected, setRefuelSelected, registerTableRefetch, setIsOpenImportModal }) => {

    // STATES

    const [openFilters, setOpenFilters] = useState<boolean>(false);
    const [selectedRefuel, setSelectedRefuel] = useState<Refuel | null>(null);
    const [detailModalOpen, setDetailModalOpen] = useState<boolean>(false);

    const service = new RefuelService();

    // HOOKS

    const { filters, updateFilters, updateFilterOrder, updatePage, updatePageSize, resetFilters } = useFiltersPR();
    const { userCan } = useContext(PrivilegeContext);
    const { handleErrors } = useHandleErrors();

    // FETCH DATA

    // -------------------------------------------------------------------------------------------------------------
    /**
     * @ES OBTIENE EL LISTADO DE REPOSTAJES
     * @EN GETS THE LIST OF REFUELS
     */
    // -------------------------------------------------------------------------------------------------------------
    const [data, loading, error, refetch] = useFetch(useCallback(async () => {
        const response = await service.listRefuels(filters);
        return response.getResponseData() as RefuelsApiResponse;
    }, [filters]));
    // -------------------------------------------------------------------------------------------------------------
    useEffect(() => {
        registerTableRefetch?.(refetch);
    }, [registerTableRefetch, refetch]);

    // FUNCTIONS

    //-------------------------------------------------------------------------------------------------------------
    /**
     * @ES ELIMINA UN REPO
     * @EN DELETES A REFUEL
     */
    //-------------------------------------------------------------------------------------------------------------
    const handleDelete = async (id: string) => {
        try {
            const response = (await service.deleteRefuel(id)).getResponseData();
            if (response.success) {
                refetch();
                toast.success('Repostaje eliminado correctamente');
            } else {
                toast.error('Error al eliminar el repostaje');
            }
        } catch (error) {
            toast.error('Error al eliminar el repostaje');
        }
    };
    //-------------------------------------------------------------------------------------------------------------

    //-------------------------------------------------------------------------------------------------------------
    /**
     * @ES ELIMINA TODOS LOS REPOSTAJES
     * @EN DELETES ALL REFUELS
     */
    //-------------------------------------------------------------------------------------------------------------
    const handleMultipleDelete = async (ids: string[]) => {
        try {
            const response = (await service.deleteMultiRefuel(ids)).getResponseData();
            if (response.success) {
                refetch();
                toast.success('Repostajes eliminados correctamente');
            } else {
                handleErrors(response);
            }
        } catch (error) {
            handleErrors(error);
        }
    };
    //-------------------------------------------------------------------------------------------------------------

    
    //-------------------------------------------------------------------------------------------------------------
    /**
     * @ES EXPORTAR REPOSTAJES
     * @EN EXPORT REFUELS
     */
    //-------------------------------------------------------------------------------------------------------------
    const handleExport = async (ids: string[]) => {
        const response = await service.exportRefuels(filters);

        const fileData = response.getResponseData();
        const blob = new Blob([fileData]);
        const url = window.URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.setAttribute('download', `repostajes_${moment().format('DD-MM-YYYY')}.xlsx`);
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        window.URL.revokeObjectURL(url);
    }
    //-------------------------------------------------------------------------------------------------------------

    //-------------------------------------------------------------------------------------------------------------
    /**
     * @ES IMPORTAR REPOSTAJES
     * @EN IMPORT REFUELS
     */
    //-------------------------------------------------------------------------------------------------------------
    const handleImport = async () => {
        setIsOpenImportModal(true);
    }
    //-------------------------------------------------------------------------------------------------------------

    //-------------------------------------------------------------------------------------------------------------
    /**
     * @ES MUESTRA LOS DATOS EN EL ORDEN CORRECTO
     * @EN SHOWS THE DATA IN THE CORRECT ORDER
     */
    //-------------------------------------------------------------------------------------------------------------
    const orderedData = () => data?.data.map((row: any) => {
        const orderedRow: any = {};
        columnOrder.forEach((key) => {
            orderedRow[key] = row[key];
        });
        return orderedRow;
    });
    //-------------------------------------------------------------------------------------------------------------

    // RENDER

    return (
        <Page container='fluid'>
            {data !== undefined && (
                <CustomTable
                    id={'refuels-table'}
                    columnOrder={columnOrder}
                    data={orderedData()}
                    floatingHorizontalScrollbar
                    isLoading={loading}
                    classNameTable='fs-table'
                    headerTableClassName='fs-table-header'
                    isSortable
                    columnsNoSortables={[]}
                    columnsNotShown={[]}
                    overrideColumns={[
                        {
                            key: 'vehicle',
                            columnNameKey: 'Vehículo',
                            render: (row: any) => {
                                const vehicle = row.row.original.vehicle;
                                const isSold = vehicle?.status === 'sold';
                                return (
                                    <div className="flex flex-col">
                                        <span className={isSold ? 'text-red-600 font-medium' : ''}>
                                            {vehicle?.plateNumber || 'Sin matrícula'}
                                        </span>
                                        <span className="text-gray-500">
                                            {vehicle?.brand} {vehicle?.model}
                                        </span>
                                        {isSold && (
                                            <span className="text-red-500 font-medium">
                                                (Vendido)
                                            </span>
                                        )}
                                    </div>
                                );
                            }
                        },
                        {
                            key:'company',
                            columnNameKey: 'Empresa',
                            permissions: [{
                                group: 'company',
                                permission: 'list'
                            }],
                            render: (row: any) => {
                                if (!row.row.original?.company) return 'N/A';
                                return (
                                    <div>
                                        {row.row.original?.company?.name}
                                    </div>
                                );
                            }
                        },
                        {
                            key: 'user',
                            columnNameKey: 'Usuario',
                            render: (row: any) => {
                                if (!row.row.original?.user) return 'N/A';
                                return (
                                    <div className="flex flex-col">
                                        <div className="font-bold">{row.row.original?.user?.name} {row.row.original?.user?.lastName}</div>
                                        <div className="text-gray-500">{row.row.original?.user?.email}</div>
                                    </div>
                                );
                            }
                        },
                        {
                            key: 'liters',
                            columnNameKey: 'Litros',
                            render: (row: any) => {
                                return (
                                    <span className="flex flex-nowrap gap-2 items-center">
                                        <BsFuelPump className="text-gray-400 min-h-[15px] min-w-[15px]" />
                                        {row.row.original?.liters ? `${row.row.original.liters} L` : 'N/A'}
                                    </span>
                                );
                            },
                        },
                        {
                            key: 'totalCost',
                            columnNameKey: 'Costo Total',
                            render: (row: any) => {
                                return (
                                    <span className="font-bold text-green-600">
                                        {row.row.original?.totalCost ? `€${row.row.original.totalCost}` : 'N/A'}
                                    </span>
                                );
                            },
                        },
                        {
                            key: 'totalKilometers',
                            columnNameKey: 'Km Totales',
                            render: (row: any) => {
                                return (
                                    <span>
                                        {row.row.original?.totalKilometers ? `${row.row.original.totalKilometers.toLocaleString()} km` : 'N/A'}
                                    </span>
                                );
                            },
                        },
                        {
                            key: 'date',
                            columnNameKey: 'Repostaje',
                            render: (row: any) => {
                                const date = row.row.original?.date?.date;
                                return date ? moment(date).format('DD/MM/YYYY') : 'N/A';
                            },
                        },
                        {
                            key: 'description',
                            columnNameKey: 'Descripción',
                            render: (row: any) => {
                                return (
                                    <span>
                                        {row.row.original?.description || 'Sin descripción'}
                                    </span>
                                );
                            },
                        },
                        {
                            key: 'photoCount',
                            columnNameKey: 'Fotos',
                            render: (row: any) => {
                                return (
                                    <span className={`px-2 py-1 rounded ${row.row.original?.photoCount > 0
                                        ? 'bg-green-100 text-green-800'
                                        : 'bg-gray-100 text-gray-800'
                                        }`}>
                                        {row.row.original?.photoCount || 0} fotos
                                    </span>
                                );
                            },
                        },
                        {
                            key: 'createdAt',
                            render: (row: any) => {
                                const date = row.row.original?.createdAt?.date;
                                return date ? moment(date).format('DD/MM/YYYY') : 'N/A';
                            },
                        },
                        {
                            key: 'updatedAt',
                            render: (row: any) => {
                                const date = row.row.original?.updatedAt?.date;
                                return date ? (
                                    <div className="text-sm text-gray-500">
                                        <Tooltip content={moment(date).format('DD/MM/YYYY HH:mm:ss')}>
                                            <span>{capitalize(formatDistance(new Date(date), new Date(), { addSuffix: true, locale: es }))}</span>
                                        </Tooltip>
                                    </div>
                                ) : 'Nunca';
                            },
                        },
                    ]}
                    actions={[
                        {
                            label: 'View',
                            permissions: { group: 'refuel', permission: 'get' },
                            render: (row: any) => {
                                return (
                                    <Tooltip
                                        size="sm"
                                        content={'Ver detalles'}
                                        placement="top"
                                        color="invert"
                                    >
                                        <ActionIcon
                                            onClick={() => {
                                                setSelectedRefuel(row);
                                                setDetailModalOpen(true);
                                            }}
                                            size="sm"
                                            variant="outline"
                                            className="hover:!border-gray-900 hover:text-gray-700 bg-white cursor-pointer w-6 h-6"
                                        >
                                            <HiOutlineEye className="h-4 w-4" size={20} />
                                        </ActionIcon>
                                    </Tooltip>
                                );
                            },
                        },
                        {
                            label: 'Edit',
                            permissions: { group: 'refuel', permission: 'edit' },
                            hide: (row: any) =>  row?.vehicle?.status === 'sold',
                            render: (row: any) => {
                                return (
                                    <Tooltip
                                        size="sm"
                                        content={'Editar'}
                                        placement="top"
                                        color="invert"
                                    >
                                        <ActionIcon
                                            onClick={() => {
                                                setRefuelSelected(row);
                                                setOpenFormDrawer(true);
                                            }}
                                            as="span"
                                            size="sm"
                                            variant="outline"
                                            className="hover:!border-gray-900 hover:text-gray-700 bg-white"
                                        >
                                            <MdOutlineModeEditOutline className="h-4 w-4" size={20} />
                                        </ActionIcon>
                                    </Tooltip>
                                );
                            },
                        },
                        {
                            label: 'Delete',
                            permissions: { group: 'refuel', permission: 'delete' },
                            render: (row: any) => {
                                return (
                                   <Tooltip
                                        size="sm"
                                        content={'Eliminar'}
                                        placement="top"
                                        color="invert"
                                    >
                                        <div>
                                            <DeletePopover
                                                title={`Eliminar`}
                                                description={`¿Estás seguro de que deseas eliminar este repostaje?`}
                                                onDelete={() => handleDelete(row.id)}
                                            />
                                        </div>
                                    </Tooltip>
                                );
                            },
                        }
                    ]}
                    handleMultipleDelete={handleMultipleDelete}
                    filters={filters}
                    updateFilters={updateFilters}
                    updateFilterOrder={updateFilterOrder}
                    defaultOrder={filters.filter_order || undefined}
                    paginationData={{
                        pageSize: filters.limit,
                        currentPage: filters.page,
                        pageCount: (data as RefuelsApiResponse) ? data.lastPage : 1,
                        totalCount: data?.totalRegisters,
                        handlePagination: updatePage,
                        handlePerPage: updatePageSize,
                    }}
                    toggleFilters={() => setOpenFilters(!openFilters)}
                >
                    {userCan('import', 'refuel') && (
                        <Tooltip content={'Importar repostajes'} placement={'top'} color={'invert'} size={'md'}>
                            <Button
                                variant={'solid'} color={'primary'}
                                size={'sm'}
                                onClick={() => { handleImport() }}
                                className='text-nowrap flex-1'
                            >
                                Importar repostajes
                            </Button>
                        </Tooltip>
                    )}
                    {userCan('export', 'refuel') && (
                        <Tooltip content={'Exportar repostajes'} placement={'top'} color={'invert'} size={'md'}>
                            <Button
                                variant={'solid'} color={'primary'}
                                size={'sm'}
                                onClick={() => { handleExport([]) }}
                                className='text-nowrap flex-1'
                            >
                                Exportar repostajes
                            </Button>
                        </Tooltip>
                    )}
                </CustomTable>
            )}

            <FilterDrawerView isOpen={openFilters} setOpenDrawer={setOpenFilters} drawerTitle={'Filtros Repostajes'} onClear={() => resetFilters(50)}>
                <RefuelsFilters filters={filters} updateFilters={updateFilters} />
            </FilterDrawerView>

            <RefuelDetailModal
                isOpen={detailModalOpen}
                onClose={() => setDetailModalOpen(false)}
                refuel={selectedRefuel}
            />

            <FormDrawerView
                isOpen={openFormDrawer}
                setOpenDrawer={setOpenFormDrawer}
                subtitleText={
                    refuelSelected ?
                        "Completa el formulario para editar los datos del repostaje" :
                        "Completa el formulario para crear un nuevo repostaje"
                }
                drawerTitle={refuelSelected ? 'Editar Repostaje' : 'Crear Repostaje'}
                submitText={refuelSelected ? 'Guardar Repostaje' : 'Crear Repostaje'}>
                <RefuelForm setOpenModal={setOpenFormDrawer} refuel={refuelSelected} refetch={refetch} />
            </FormDrawerView>
        </Page>
    );
};

export default RefuelsList;